import { useDispatch, useSelector } from 'react-redux'
import { stepIncrement, stepComplete, setFinalCase } from '../../../../redux/steps'
import StatusChip from '../../../StatusChip'
import { Button } from '../../../Button'
import { STATUS_INFO, FINAL_CASES } from './helpers'
import { useFinishStudy } from '../../../../hooks/NewCase/useFinishStudy'
import { toggleToNewStatus, removeIncompleteEvaluations } from '../../../../redux/user'
import { resetEvaluations } from '../../../../redux/evaluations'
import { useOpenPDF } from '../../../../hooks/useOpenPDF'
import { pdfDisponible } from '../../../../utils/pdfDisponible'
import {
  Container,
  StatusImage,
  StatusTitle,
  StatusSubtitle,
  StatusChipText,
  FooterWrapper,
  ObservationTitle,
  ObservationTextArea,
  ButtonsWrapper
} from './styles'
import GenericModal from '../../../Modal/GenericModal'
import { useState } from 'react'
import { clearMotivoMalaCalidad } from '../../../../redux/motivoMalaCalidad'
import { colors, derivedColors } from '../../../../const'

export function AnalysisResult ({ status }) {
  const dispatch = useDispatch()
  const motivoMalaCalidad = useSelector(state => state.motivoMalaCalidad)
  const { evaluations } = useSelector(state => state)
  const isSampled = status === 'nonReferable' && evaluations?.control_calidad_muestreo === true
  const analysisStatus = STATUS_INFO[isSampled ? 'nonReferableSampled' : status]
  const { finishStudy } = useFinishStudy()
  const { openPDF } = useOpenPDF(`estudios/descargar/${evaluations?.id}`)
  const [inputModalOpen, setInputModalOpen] = useState(false)
  const [noInputModalOpen, setNoInputModalOpen] = useState(false)
  const [observacion, setObservacion] = useState('')

  const pdfListo = pdfDisponible(evaluations)

  const handleDownloadPDF = async (button) => {
    if (!pdfListo || !button.includes('Descargar')) {
      return
    }
    try {
      await openPDF()
    } catch (error) {
      console.error('Error al descargar el PDF:', error)
    }
  }

  const handleSendCase = async (button, motivo = '') => {
    let finalCase = null

    // Mostrar modal si hace falta motivo
    if (!isSampled && !motivo && button === analysisStatus.button2) {
      if (status === 'referable') {
        setNoInputModalOpen(true)
        return
      } else if (status === 'nonReferable') {
        setInputModalOpen(true)
        return
      }
    }

    const observacionLimpia = observacion.trim()
    const motivoDecision = motivo ? String(motivo).trim() : undefined

    // motivo es lo que viene del modal (discrepancia técnico vs IA) → backend persiste motivo_decision_tecnico
    if (isSampled && button === analysisStatus.button3) {
      await finishStudy(true, observacionLimpia, motivoMalaCalidad)
      finalCase = FINAL_CASES[4]
    } else if (status === 'referable' && motivo) {
      await finishStudy(false, observacionLimpia, motivoMalaCalidad, motivoDecision)
      finalCase = FINAL_CASES[1]
    } else if (status === 'nonReferable' && motivo) {
      await finishStudy(true, observacionLimpia, motivoMalaCalidad, motivoDecision)
      finalCase = FINAL_CASES[2]
    } else if (status === 'referable') {
      await finishStudy(true, observacionLimpia, motivoMalaCalidad)
      finalCase = FINAL_CASES[0]
    } else if (status === 'nonReferable') {
      await finishStudy(false, observacionLimpia, motivoMalaCalidad)
      finalCase = FINAL_CASES[3]
    }

    if (finalCase) {
      dispatch(setFinalCase(finalCase))
    }

    dispatch(removeIncompleteEvaluations())
    dispatch(resetEvaluations())
    dispatch(toggleToNewStatus())
    dispatch(stepComplete())
    dispatch(clearMotivoMalaCalidad())
  }

  return (
    <Container>
      <StatusImage height='66' src={analysisStatus.icon} />
      <StatusTitle>{analysisStatus.title}</StatusTitle>
      <StatusSubtitle>{analysisStatus.subtitle1}</StatusSubtitle>
      <StatusSubtitle>{analysisStatus.subtitle2}</StatusSubtitle>

      <FooterWrapper>
        <StatusChip type='primary' size='small'>
          <StatusChipText>
            Esta evaluación no reemplaza el diagnóstico de un oftalmólogo.
          </StatusChipText>
          <StatusChipText>
            Es una valoración de la IA de Retinar.
          </StatusChipText>
        </StatusChip>

        <ObservationTitle>
          Si querés hacer alguna observación, por favor, escribila acá:
        </ObservationTitle>
        <ObservationTextArea
          value={observacion}
          onChange={e => setObservacion(e.target.value)}
        />

        <ButtonsWrapper>
          <Button
            color='secondary'
            disabled={!pdfListo}
            title={pdfListo ? undefined : 'Preparando PDF...'}
            aria-label={pdfListo ? analysisStatus.button1 : 'Preparando PDF...'}
            onClick={() => handleDownloadPDF(analysisStatus.button1)}
          >
            {analysisStatus.button1}
          </Button>
          {analysisStatus.button2 && (
            <Button color='secondary' onClick={() => handleSendCase(analysisStatus.button2)}>
              {analysisStatus.button2}
            </Button>
          )}
          <Button color='primary' onClick={() => handleSendCase(analysisStatus.button3)}>
            {analysisStatus.button3}
          </Button>
        </ButtonsWrapper>
      </FooterWrapper>

      {/* Modal para info complementaria (con input) */}
      <GenericModal
        isOpen={inputModalOpen}
        onClose={() => {
          setInputModalOpen(false)
        }}
        withInput
        title={(status === 'referable') ? 'Motivo de no derivación' : 'Motivo de Derivación'}
        description={
          (status === 'referable')
            ? 'Para completar el registro, por favor contanos la razón por la cual este examen no será derivado a un oftalmólogo. Esta información puede ayudar en el futuro a hacer un mejor seguimiento del caso.'
            : 'Para completar el registro, por favor contanos por qué vas a derivar este examen. Esta información ayudará a un mejor seguimiento del caso.'
        }
        inputTitle='Justificación (requerida):'
        onSave={(reason) => {
          handleSendCase(analysisStatus.button2, reason)
          setInputModalOpen(false)
        }}
        saveButtonText='Guardar motivo y finalizar'
        cancelButtonText='Cancelar'
      />

      {/* Modal de confirmación (sin input) */}
      <GenericModal
        isOpen={noInputModalOpen}
        onClose={() => setNoInputModalOpen(false)}
        withInput={false}
        title='¿Finalizar sin derivar a especialistas?'
        description='Si cerrás este examen sin derivarlo, ningún oftalmólogo va a confirmar el diagnóstico. ¿Estás seguro de que querés continuar?'
        onSave={() => {
          setNoInputModalOpen(false)
          setInputModalOpen(true)
        }}
        saveButtonText='Sí, finalizar sin derivar'
        cancelButtonText='Cancelar'
        saveButtonColor={{ bg: colors.error, text: colors.surface, hover: derivedColors.errorHover }}
        cancelButtonColor={{ bg: colors.pageBackground, text: colors.textPrimary, hover: colors.borderSubtle }}
      />
    </Container>
  )
}

export default AnalysisResult
